import React, { useState, useEffect, useMemo } from 'react';
import { MDBCol, MDBRow } from 'mdbreact';
import { Redirect } from 'react-router-dom';
import { withFormik } from 'formik';
import { post } from 'axios';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import moment from 'moment';

import { actionSvgs } from 'assets/icons';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';

import FeaturedText from 'components/FeaturedText';
import Footer from 'components/Footer';
import Overlay from 'components/Informations/Overlay';
import ReturnPreviousPage from 'components/ReturnPreviousPage';
import Toast from 'components/Informations/Toast';

import DateField from 'components/FormGroup/DateField';
import ErrorMessage from 'components/FormGroup/ErrorMessage';
import InputField from 'components/FormGroup/InputField';
import SelectField from 'components/FormGroup/SelectField';
import Switch from 'components/FormGroup/Switch';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';
import { salesForceInputs, ProjectIdInput } from 'utils/masks';

import { sortArrayInAlphabeticalOrder } from 'utils/objectManipulations';

import {
  handleCoordinatorDelete,
  handleCoordinatorInsert,
  handleCoordinatorUpdate,
} from '../../utils';
import * as S from './styles';

const validationSchema = Yup.object().shape({
  identification: Yup.string()
    .min(9, 'Número de identificação incorreto')
    .max(12, 'Número de identificação incorreto')
    .required('Insira a identificação do projeto'),
  projectName: Yup.string().required('Insira o nome do projeto'),
  clientID: Yup.string().required('Insira o nome do cliente'),
  projectCategory: Yup.string().required(
    'Selecione uma categoria para o projeto',
  ),
  coustCenter: Yup.string().required('Selecione o centro de custos do projeto'),
  projectStatus: Yup.string().required('Selecione o status atual do projeto'),
  expectedInitialDate: Yup.string().required('Insira a data inicial prevista'),
  expectedFinalDate: Yup.string().required('Insira a data final prevista'),
  technicalResponsible: Yup.string(),
  projectManager: Yup.string().required('Selecione um gerente de projetos'),
  accountManager: Yup.string(),
  clientRepresentative: Yup.string(),
});

const {
  getEmployeesToSelect,
  statusCatProjeto,
  getClients,
  insertProject,
  getCoustCenterOptions,
} = endpoints;

function CreateNewProject({
  handleSubmit,
  errors,
  values,
  setFieldValue,
  status,
  history,
  location,
  user,
}) {
  const { state } = location;

  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [overlayType, setOverlayType] = useState('');

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({});

  const [formAlterations, setFormAlterations] = useState(false);
  const [expectedInitialDate, setExpectedInitialDate] = useState(new Date());
  const [expectedFinalDate, setExpectedFinalDate] = useState(new Date());

  const [coustCenterOptions, setCoustCenterOptions] = useState([]);
  const [selectOptions, setSelectOptions] = useState({});

  const [coordinatorsList, setCoordinatorsList] = useState([
    { id_funcionario_coordenador: -1 },
  ]);

  const [isUniversal, setIsUniversal] = useState(false);
  const [isClientAttendance, setIsClientAttendance] = useState(false);

  const [clientsList] = useFetch(getClients);
  const [employeesList] = useFetch(getEmployeesToSelect);
  const [getCoustCenters] = useFetch(getCoustCenterOptions);
  const [statusCategoriaList] = useFetch(statusCatProjeto);

  useEffect(() => {
    const toast = !!(status && status?.toast);

    if (toast) {
      setToastVisibility(true);
      setToastInfos(status?.toast);
    }
  }, [status]);

  const memoizedErrors = useMemo(() => {
    const error = !!(status && status?.coordinatorError);
    return error;
  }, [status]);

  const handleMix = (currentArray, arrayToMix) =>
    arrayToMix.map(({ id_funcionario: id, nome_funcao: funcao }) =>
      currentArray.map(current => {
        if (current.id_funcionario_coordenador === id) {
          return {
            ...current,
            error: true,
            funcao,
          };
        }

        return '';
      }),
    );

  useEffect(() => {
    const error = !!(status && status?.coordinatorError);

    if (!error) {
      const { coordinatorError } = status;
      if (coordinatorError && coordinatorsList) {
        const mergedErrors = handleMix(coordinatorsList, coordinatorError)
          .flat()
          .filter(item => !!item);
        setCoordinatorsList(mergedErrors);
      }
    }
  }, [memoizedErrors, coordinatorsList, status]);

  useEffect(() => {
    const clients = clientsList
      ?.filter(client => client.id_status_cliente === 1)
      .map(client => ({ value: client.id_cliente, label: client.nome }));

    const withoutRecourse = employeesList?.filter(
      employee => employee.nome.toLowerCase() === 'sem recurso',
    )[0];

    const findEmployeesByFunctionName = (functionName, variantName) =>
      employeesList
        .filter(({ nome_funcao }) => {
          const name = nome_funcao.toLowerCase();
          return name.includes(functionName) || name.includes(variantName);
        })
        ?.map(employee => ({
          value: employee.id_funcionario,
          label: employee.nome,
        }));

    const allAccountManagers = findEmployeesByFunctionName('account');
    const allCoordinators = findEmployeesByFunctionName('coordenador');
    const allTechLeads = findEmployeesByFunctionName('lead');
    // const allScrumMasters = findEmployeesByFunctionName('scrum');
    // const allDeliveryManagers = findEmployeesByFunctionName('delivery');
    // const allDirectors = findEmployeesByFunctionName('diretor');
    // const allProjectManagers = findEmployeesByFunctionName(
    //   'gerente de projetos',
    // );
    const isGP = employeesList
      .filter(employee => !!employee.is_gp)
      ?.map(employee => ({
        value: employee.id_funcionario,
        label: employee.nome,
      }));

    setSelectOptions(prev => ({
      ...prev,
      technical: [...allTechLeads, ...allCoordinators],
      projectManagers: [...isGP],
      clients,
      accountManagers: [
        ...allAccountManagers,
        {
          label: withoutRecourse?.nome,
          value: withoutRecourse?.id_funcionario,
        },
      ],
    }));
  }, [employeesList, clientsList]);

  useEffect(() => {
    setFieldValue('user', user);

    (async () => {
      const statusCategory = await statusCategoriaList;
      const coustCenters = await getCoustCenters;
      const { categoriaProjeto, statusProjeto } = statusCategory;

      const categories =
        categoriaProjeto &&
        categoriaProjeto.map(item => ({
          value: item.id_categoria_projeto,
          label: item.nome_categoria_projeto,
        }));

      const projectStatus =
        categoriaProjeto &&
        statusProjeto.map(item => ({
          value: item.id_status_projeto,
          label: item.tipo_status,
        }));

      setCoustCenterOptions(
        coustCenters.map(item => ({ ...item, value: item.id })),
      );

      setCoustCenterOptions(
        coustCenters.map(item => ({ ...item, value: item.id })),
      );
      setSelectOptions(prev => ({
        ...prev,
        categories,
        projectStatus,
      }));
    })();

    return () => {
      localStorage.removeItem('toastError');
    };
  }, [
    employeesList,
    clientsList,
    statusCategoriaList,
    getCoustCenters,
    setFieldValue,
    user,
  ]);

  const renderOverlay = type => {
    switch (type) {
      case 'addProject':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>cadastrando</span>,
              'um novo projeto e esse processo ',
              <span>não poderá ser revertido</span>,
              '. Deseja continuar com a criação?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
                history.push('/auth/coordinate-projects', state);
              }}
            />
            <FilledButton
              text="Não, vou verificar"
              buttonClass="warn"
              handleClick={() => setOverlayVisibility(!overlayVisibility)}
            />
          </Overlay>
        );

      case 'cancel':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>cancelando</span>,
              ' a inserção de um novo projeto e esta ação ',
              <span>descartará</span>,
              ' todas as informações não salvas até o momento. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
                history.push('/auth/coordinate-projects', state);
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => setOverlayVisibility(!overlayVisibility)}
            />
          </Overlay>
        );

      case 'returning':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>voltando para a tela anterior</span>,
              ' e esta ação irá ',
              <span>descartar</span>,
              ' as vendas que não foram salvas. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayVisibility(!overlayVisibility);
                setOverlayType('');
                history.push('/auth/coordinate-projects', state);
              }}
            />
            <FilledButton
              text="Não, permanecer"
              buttonClass="warn"
              handleClick={() => setOverlayVisibility(!overlayVisibility)}
            />
          </Overlay>
        );

      default:
        return '';
    }
  };

  const renderToast = (type, message, autoClose) => {
    setTimeout(() => {
      setToastVisibility(false);
      setToastInfos({});
    }, autoClose + 1500);

    return (
      <Toast type={type} message={message} autoClose={autoClose} hideProgress />
    );
  };

  return (
    <>
      {overlayVisibility && renderOverlay(overlayType)}

      {Object.keys(status).length && status.success ? (
        <Redirect to={{ pathname: '/auth/coordinate-projects', state }} />
      ) : (
        ''
      )}

      {toastVisibility &&
        renderToast(
          toastInfos.toastType,
          toastInfos.toastMessage,
          toastInfos.toastCloseTime,
        )}

      {Object.keys(selectOptions).length && (
        <S.Container>
          <MDBRow>
            <MDBCol lg="12">
              <ReturnPreviousPage
                text="Voltar para gerência de projetos"
                handleClick={() => {
                  const haveAlterations = () => {
                    setOverlayVisibility(true);
                    setOverlayType('returning');
                  };

                  const dontHaveAlterations = () => {
                    setToastVisibility(false);
                    history.push('/auth/coordinate-projects', state);
                  };

                  return formAlterations
                    ? haveAlterations()
                    : dontHaveAlterations();
                }}
              />
              <S.Introduction>
                <S.DescriptionText>Cadastro de projetos</S.DescriptionText>
                <FeaturedText text="Cadastre novos projetos à serem incorporados no sistema da empresa" />
              </S.Introduction>
            </MDBCol>
          </MDBRow>
          <MDBRow>
            <MDBCol lg="6" sm="12">
              <S.Divider>Dados do projeto</S.Divider>
              <S.Form onSubmit={handleSubmit}>
                <InputField
                  labelText="Identificação (*)"
                  width={50}
                  placeholderText="Número de identificação"
                  value={values.identification}
                  name="identification"
                  handleChange={e => {
                    setFieldValue(
                      'identification',
                      ProjectIdInput(e.target.value),
                    );
                    setFormAlterations(true);
                  }}
                />
                {!!errors.identification && (
                  <ErrorMessage message={errors.identification} />
                )}

                <InputField
                  labelText="Nome do projeto (*)"
                  placeholderText="Insira o nome do projeto"
                  value={values.projectName}
                  name="projectName"
                  handleChange={e => {
                    setFieldValue('projectName', e.target.value);
                    setFormAlterations(true);
                  }}
                />
                {!!errors.projectName && (
                  <ErrorMessage message={errors.projectName} />
                )}

                <SelectField
                  options={sortArrayInAlphabeticalOrder(
                    selectOptions?.clients,
                    'label',
                  )}
                  value={selectOptions?.clients?.find(
                    option => option.value === values.clientID,
                  )}
                  handleChange={({ value }) => {
                    setFieldValue('clientID', value);
                    setFormAlterations(true);
                  }}
                  hasLabelMargin
                  labelText="Nome do cliente (*)"
                  placeholderText="Insira o nome do cliente"
                  name="clientID"
                />
                {!!errors.clientID && (
                  <ErrorMessage message={errors.clientID} />
                )}

                <SelectField
                  options={sortArrayInAlphabeticalOrder(
                    selectOptions?.categories,
                    'label',
                  )}
                  value={selectOptions?.categories?.find(
                    option => option.value === values.projectCategory,
                  )}
                  handleChange={({ value }) => {
                    setFieldValue('projectCategory', value);
                    setFormAlterations(true);
                  }}
                  hasLabelMargin
                  labelText="Categoria do projeto (*)"
                  placeholderText="Selecione a categoria do projeto"
                  name="projectCategory"
                />
                {!!errors.projectCategory && (
                  <ErrorMessage message={errors.projectCategory} />
                )}

                <SelectField
                  hasLabelMargin
                  labelText="Centro de custos (*)"
                  placeholderText="Selecione a localização..."
                  name="coustCenter"
                  options={sortArrayInAlphabeticalOrder(
                    coustCenterOptions,
                    'label',
                  )}
                  value={coustCenterOptions.find(
                    option => option.value === values.coustCenter,
                  )}
                  handleChange={({ value }) => {
                    setFieldValue('coustCenter', value);
                    setFormAlterations(true);
                  }}
                />
                {errors.coustCenter && (
                  <ErrorMessage message={errors.coustCenter} />
                )}

                <SelectField
                  options={sortArrayInAlphabeticalOrder(
                    selectOptions?.projectStatus,
                    'label',
                  )}
                  value={selectOptions?.projectStatus?.find(
                    option => option.value === values.projectStatus,
                  )}
                  handleChange={({ value }) => {
                    setFieldValue('projectStatus', value);
                    setFormAlterations(true);
                  }}
                  hasLabelMargin
                  labelText="Status do projeto (*)"
                  placeholderText="Selecione o status atual do projeto"
                  name="projectStatus"
                />
                {!!errors.projectStatus && (
                  <ErrorMessage message={errors.projectStatus} />
                )}

                <S.FlexRow>
                  <Switch
                    id="isUniversal"
                    onChange={() => {
                      const actualValue = isUniversal;
                      setIsUniversal(current => !current);
                      setFieldValue('isUniversal', !actualValue);
                    }}
                    isChecked={isUniversal}
                    labelText="Projeto universal"
                  />
                  <Switch
                    id="isClientAttendance"
                    onChange={() => {
                      const actualValue = isClientAttendance;
                      setIsClientAttendance(current => !current);
                      setFieldValue('isClientAttendance', !actualValue);
                    }}
                    isChecked={isClientAttendance}
                    labelText="Atendimento ao cliente"
                  />
                </S.FlexRow>

                <S.Divider>Datas do projeto</S.Divider>
                <S.FlexRow>
                  <div>
                    <DateField
                      handleChange={e => {
                        setFieldValue('expectedInitialDate', moment(e));
                        setExpectedInitialDate(e);
                        setFormAlterations(true);
                      }}
                      selectedValue={expectedInitialDate}
                      placeholderText="Insira a data"
                      onlyMonth={false}
                      name="expectedInitialDate"
                      labelText="Data de início prevista (*)"
                    />
                    {!!errors.expectedInitialDate && (
                      <ErrorMessage message={errors.expectedInitialDate} />
                    )}
                  </div>
                  <div>
                    <DateField
                      handleChange={e => {
                        setFieldValue('expectedFinalDate', moment(e));
                        setExpectedFinalDate(e);
                        setFormAlterations(true);
                      }}
                      selectedValue={expectedFinalDate || new Date()}
                      placeholderText="Insira a data"
                      onlyMonth={false}
                      name="expectedFinalDate"
                      labelText="Data final prevista (*)"
                    />
                    {!!errors.expectedFinalDate && (
                      <ErrorMessage message={errors.expectedFinalDate} />
                    )}
                  </div>
                </S.FlexRow>
                <S.Divider>Informações de responsabilidade</S.Divider>
                <S.CoordinatorContainer>
                  {coordinatorsList?.map((coordinator, index) => (
                    <S.CoordinatorBlock
                      className={coordinator.error ? 'error' : ''}
                    >
                      <SelectField
                        options={sortArrayInAlphabeticalOrder(
                          selectOptions?.technical,
                          'label',
                        )}
                        value={selectOptions?.technical.find(
                          option =>
                            option.value ===
                              values?.technicalResponsible[index]
                                ?.id_funcionario || '',
                        )}
                        handleChange={({ value }) => {
                          const currentValues = values.technicalResponsible;
                          currentValues[index] = { id_funcionario: value };

                          setFieldValue('technicalResponsible', [
                            ...currentValues,
                          ]);
                          setFormAlterations(true);

                          return handleCoordinatorUpdate(
                            index,
                            value,
                            setCoordinatorsList,
                          );
                        }}
                        hasLabel
                        labelText={`${index + 1}º Responsável técnico`}
                        placeholderText="Selecione o responsável técnico"
                        name={`technicalResponsible-${index}`}
                      />
                      {coordinator.error && (
                        <ErrorMessage
                          message={`Pode existir apenas 1 ${coordinator.funcao} por projeto`}
                        />
                      )}

                      {!!errors.technicalResponsible && (
                        <ErrorMessage message={errors.technicalResponsible} />
                      )}

                      <S.RemoveCoordinator
                        onClick={() => {
                          const currentValues = values.technicalResponsible;
                          const removed = currentValues.filter(
                            ({ id_funcionario: id }) =>
                              id !== coordinator.id_funcionario_coordenador,
                          );

                          setFieldValue('technicalResponsible', [...removed]);
                          return handleCoordinatorDelete(
                            coordinator.id_funcionario_coordenador,
                            setCoordinatorsList,
                          );
                        }}
                        disabled={coordinatorsList?.length <= 1}
                      >
                        {actionSvgs('remove')}
                        Remover
                      </S.RemoveCoordinator>
                    </S.CoordinatorBlock>
                  ))}

                  <FilledButton
                    handleClick={() => {
                      const currentValues = values.technicalResponsible;
                      setFieldValue('technicalResponsible', [
                        ...currentValues,
                        { id_funcionario: -1 },
                      ]);

                      return handleCoordinatorInsert(setCoordinatorsList);
                    }}
                    text="+ Adicionar coodenador"
                  />
                </S.CoordinatorContainer>

                <SelectField
                  options={sortArrayInAlphabeticalOrder(
                    selectOptions?.projectManagers,
                    'label',
                  )}
                  value={selectOptions?.projectManagers?.find(
                    option => option.value === values.projectManager,
                  )}
                  handleChange={({ value }) => {
                    setFieldValue('projectManager', value);
                    setFormAlterations(true);
                  }}
                  hasLabelMargin
                  labelText="Gerente de projetos (*)"
                  placeholderText="Selecione o gerente de projetos"
                  name="projectManager"
                />
                {!!errors.projectManager && (
                  <ErrorMessage message={errors.projectManager} />
                )}

                <SelectField
                  options={sortArrayInAlphabeticalOrder(
                    selectOptions?.accountManagers,
                    'label',
                  )}
                  value={selectOptions?.accountManagers?.find(
                    option => option.value === values.accountManager,
                  )}
                  handleChange={({ value }) => {
                    setFieldValue('accountManager', value);
                    setFormAlterations(true);
                  }}
                  hasLabelMargin
                  labelText="Gerente de contas"
                  placeholderText="Selecione o gerente de contas"
                  name="accountManager"
                />
                {!!errors.accountManager && (
                  <ErrorMessage message={errors.accountManager} />
                )}

                <InputField
                  labelText="Representante do cliente"
                  placeholderText="Digite o nome do representante"
                  value={values.clientRepresentative}
                  name="clientRepresentative"
                  handleChange={e => {
                    setFieldValue('clientRepresentative', e.target.value);
                    setFormAlterations(true);
                  }}
                />
                {!!errors.clientRepresentative && (
                  <ErrorMessage message={errors.clientRepresentative} />
                )}

                <S.Buttons>
                  <FilledButton
                    text="Cancelar"
                    buttonClass="cancel"
                    handleClick={() => {
                      setOverlayVisibility(true);
                      setOverlayType('cancel');
                    }}
                  />
                  <FilledButton
                    text="Cadastrar projeto"
                    buttonType="submit"
                    buttonClass="confirm"
                  />
                </S.Buttons>
              </S.Form>
            </MDBCol>
          </MDBRow>
        </S.Container>
      )}
      <Footer />
    </>
  );
}

CreateNewProject.defaultProps = {
  status: {},
  location: {},
  user: {},
};

CreateNewProject.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.objectOf(PropTypes.string).isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  status: PropTypes.objectOf(PropTypes.string),
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};

export default withFormik({
  mapPropsToValues: () => ({
    technicalResponsible: [],
    atendimento_ao_cliente: 0,
    projeto_universal: 0,
  }),

  validateOnBlur: false,
  validateOnChange: false,

  validationSchema,

  handleSubmit: (values, { setStatus }) => {
    const {
      user,
      accountManager,
      clientRepresentative,
      identification,
      projectName,
      clientID,
      projectCategory,
      coustCenter,
      projectStatus,
      expectedInitialDate,
      expectedFinalDate,
      technicalResponsible,
      projectManager,
      isUniversal,
      isClientAttendance,
    } = values;

    const { id_funcionario: idFuncionario } = user;

    const toSend = {
      id_usr_in: idFuncionario,
      id_usr_up: idFuncionario,
      identificacao: identification,
      nome: projectName,
      responsavel: clientRepresentative,
      dt_inicio: expectedInitialDate,
      dt_final: expectedFinalDate,
      dt_inicio_prevista: expectedInitialDate,
      dt_final_prevista: expectedFinalDate,
      id_status_projeto: projectStatus,
      id_cliente: clientID,
      coordenadores: technicalResponsible,
      id_func_gp_smtx: projectManager,
      id_acc_manager: accountManager,
      id_categoria_projeto: projectCategory,
      id_centro_de_custo: coustCenter,
      atendimento_ao_cliente: isClientAttendance ? 1 : 0,
      projeto_universal: isUniversal ? 1 : 0,
    };

    post(insertProject, { ...toSend })
      .then(() => setStatus({}))
      .then(() =>
        localStorage.setItem(
          'successToast',
          JSON.stringify({
            toastType: 'success',
            toastMessage: 'Projeto criado com sucesso',
            toastCloseTime: 8000,
          }),
        ),
      )
      .then(() => setStatus({ success: true }))
      .catch(e => {
        if (e.response?.status === 500 && !e.response?.data?.coordenadores) {
          return setStatus({
            toast: {
              toastVisiblity: true,
              toastType: 'error',
              toastMessage: 'Número de identificação já cadastrado',
              toastCloseTime: 8000,
            },
          });
        }

        if (e.response?.data?.coordenadores) {
          const {
            data: { coordenadores },
          } = e.response;

          return setStatus({
            toast: {
              toastVisiblity: true,
              toastType: 'error',
              toastMessage: 'Coordenadores não podem possuir a mesma função',
              toastCloseTime: 8000,
            },
            coordinatorError: coordenadores,
          });
        }

        return setStatus({
          toast: {
            toastVisiblity: true,
            toastType: 'error',
            toastMessage:
              'Erro ao cadastrar o projeto. Por favor tente novamente',
            toastCloseTime: 8000,
          },
        });
      });
  },
})(CreateNewProject);
